import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { workbooksApi } from "../api/workbooks";
import { startCheckout } from "../api/payment";
import {
  catalogueApi,
  bundleSlugForRoute,
  workbookProductFor,
  rupees,
} from "../api/catalogue";
import { workbooksForBundle, isBundleOwned } from "../data/workbookBundles";
import Navbar from "../components/Navbar";
import WorkbookBreadcrumb from "../components/WorkbookBreadcrumb";
import LoadErrorPanel from "../components/LoadErrorPanel";
import {
  settled,
  isAnswered,
  isMissing,
  anyFailed,
  LOADING_STATE,
} from "../lib/fetchState";

/*
  Bundle detail page. Route: /workbooks/bundle/:track
  View only sales page reached by non-owners. Lists the track's workbooks as a
  static read only list, no section drilldown and no getOne. Rail shows price
  and Get this track, except csharp which is free to read. Owners are routed to
  the library by the catalog instead. No backend changes. No em or en dashes.
*/

const C = { cream:"#F5F2EC", card:"#FFFFFF", navy:"#1a1a2e", navySoft:"#1e2a3a", blue:"#2563EB", orange:"#EA580C", line:"#E4DFD4", muted:"#6B6458" };
const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");

const I = {
  Lock:(p)=>(<svg width={p.size||16} height={p.size||16} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>),
  Book:(p)=>(<svg width={p.size||16} height={p.size||16} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>),
  Layers:(p)=>(<svg width={p.size||16} height={p.size||16} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>),
  Clock:(p)=>(<svg width={p.size||16} height={p.size||16} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>),
  Refresh:(p)=>(<svg width={p.size||16} height={p.size||16} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>),
  Check:(p)=>(<svg width={p.size||16} height={p.size||16} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth={p.strokeWidth||2} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>),
  Down:(p)=>(<svg width={p.size||18} height={p.size||18} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>),
  Right:(p)=>(<svg width={p.size||18} height={p.size||18} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>),
  Play:(p)=>(<svg width={p.size||13} height={p.size||13} viewBox="0 0 24 24" fill={p.fill||"none"} stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>),
  Bookmark:(p)=>(<svg width={p.size||15} height={p.size||15} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>),
  Share:(p)=>(<svg width={p.size||15} height={p.size||15} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>),
  Users:(p)=>(<svg width={p.size||16} height={p.size||16} viewBox="0 0 24 24" fill="none" stroke={p.color||"currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
};

// Hero code syntax colors. Each heroCode line is an array of [text, colorKey]
// tokens: "kw" keyword (blue), "fn" method (orange), "cm" comment, "" default.
const HCODE = { kw:"#7aa2ff", fn:"#ffb86b", cm:"#6f7d93" };

// The bundle's copy, price, product code and membership all arrive from
// /api/bundles. This page used to carry its own BUNDLE_CONFIG: a second copy of
// the catalogue's titles, taglines, accents and product keys, which had to be
// edited in step with three other copies or the page sold the wrong SKU.
//
// "Coming soon" is likewise the server's answer now. It used to be a local rule
// ("fewer than three workbooks"), invented here, that the backend never knew
// about, so a track could look unavailable on this page and still be bought
// from another.

export default function BundleDetail() {
  const { track } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cfg, setCfg] = useState(null);
  const [workbooks, setWorkbooks] = useState([]);
  const [ownedBundleSlugs, setOwnedBundleSlugs] = useState([]);
  const [bundleStats, setBundleStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // How each read went, kept apart from what it returned. Ownership decides
  // whether this page sells or redirects, so "the server did not answer" must
  // never collapse into "you do not own this": that is how a blip put a live buy
  // button in front of a buyer who already owned the track.
  const [ownershipState, setOwnershipState] = useState(LOADING_STATE);
  const [bundleState, setBundleState] = useState(LOADING_STATE);
  // Which tab is open, and which workbooks are expanded, live in the URL. They
  // were useState, so coming back from a workbook reset the page to Overview with
  // everything collapsed, and neither could be linked to.
  const TABS = ["info", "reviews"];
  const tabParam = searchParams.get("tab");
  const tab = TABS.includes(tabParam) ? tabParam : "info";

  // replace, not push: a tab is a view of this page, not a place. Pushing meant
  // three tab clicks put three entries between the reader and the way back.
  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "info") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };
  // Expandable section list: which workbook slugs are open comes from ?open= as a
  // comma separated list. The lazy cache of their sections stays in state: it is
  // fetched data, not a view the user chose.
  const expandedSlugs = useMemo(
    () =>
      new Set(
        (searchParams.get("open") || "")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      ),
    [searchParams],
  );
  const [sectionsBySlug, setSectionsBySlug] = useState({});

  // A ref rather than an effect-scoped flag, so Retry can reuse the same loader.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      settled(workbooksApi.getAll()),
      settled(catalogueApi.getBundle(bundleSlugForRoute(track))),
    ]).then(([ownership, bundle]) => {
      if (!aliveRef.current) return;
      setOwnershipState(ownership);
      setBundleState(bundle);
      // Only an answer is allowed to overwrite what is on screen. A failed read
      // leaves the previous values alone rather than blanking them.
      if (isAnswered(ownership)) {
        setWorkbooks(ownership.data?.workbooks || []);
        setOwnedBundleSlugs(ownership.data?.ownedBundleSlugs || []);
        setBundleStats(ownership.data?.bundleStats || null);
      }
      if (isAnswered(bundle)) setCfg(bundle.data || null);
      setLoading(false);
    });
  }, [track]);

  useEffect(() => {
    setOwnershipState(LOADING_STATE);
    setBundleState(LOADING_STATE);
    load();
  }, [load]);

  // Ownership, re-read on demand rather than only at mount.
  //
  // A purchase changes what the server says this user owns, but nothing about
  // this page's state, so without a re-read the buy button stayed live over
  // content the buyer had just paid for - and clicking it opened Razorpay
  // again. Only /api/workbooks is refetched: the bundle's sales copy cannot
  // have changed, and ownership is the one thing that has.
  const refreshOwnership = useCallback(() => {
    // Returned, not fired and forgotten: startCheckout awaits this before it
    // navigates, so the destination is chosen against the server's answer.
    return settled(workbooksApi.getAll()).then((ownership) => {
      if (!aliveRef.current) return;
      setOwnershipState(ownership);
      // A failed refresh keeps whatever was already known. Downgrading to "owns
      // nothing" here would be worst of all: it happens right after a payment.
      if (!isAnswered(ownership)) {
        console.error("Ownership refresh failed:", ownership.error);
        return;
      }
      setWorkbooks(ownership.data?.workbooks || []);
      setOwnedBundleSlugs(ownership.data?.ownedBundleSlugs || []);
      setBundleStats(ownership.data?.bundleStats || null);
    });
  }, []);

  // Coming back to the tab is the webhook-only case: on mobile UPI the buyer
  // leaves for their bank app, the webhook confirms and grants while they are
  // away, and the handler that would have told us never runs. Whatever they
  // did while backgrounded, the server knows; ask it.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshOwnership();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshOwnership]);

  // Ownership, same rule as the catalog and the library, from one helper:
  // owners belong in the library, not the buy page. Covers a just-purchased
  // reload that lands back on this detail URL.
  //
  // Gated on the ownership read having actually answered. Without that, a failed
  // read reads as "not an owner", which is the wrong answer in the one direction
  // that costs money.
  const owned =
    isAnswered(ownershipState) && isBundleOwned(cfg, ownedBundleSlugs);

  // Redirect a now-owner to their library, once, after data loads.
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (loading || !owned || redirectedRef.current) return;
    redirectedRef.current = true;
    navigate(`/workbooks/bundle/${track}/library`, { replace: true });
  }, [loading, owned, track, navigate]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ background:C.cream, minHeight:"100vh", display:"grid", placeItems:"center", color:C.muted, fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif" }}>Loading...</div>
      </>
    );
  }

  // A read that did not come back. Nothing below this point may render: the page
  // cannot know whether this visitor owns the track, and every branch after here
  // either sells it to them or decides they do not own it.
  if (anyFailed(ownershipState, bundleState)) {
    return (
      <>
        <Navbar />
        <LoadErrorPanel
          title="We could not load this track"
          message="Something went wrong reaching the server, so we cannot show this track or what you already own. Nothing has been charged."
          onRetry={load}
          retrying={loading}
          background={C.cream}
          accent={C.blue}
          ink={C.navy}
          muted={C.muted}
        />
      </>
    );
  }

  // 404, which is an answer: this track really does not exist.
  if (isMissing(bundleState) || !cfg) {
    return (
      <>
        <Navbar />
        <div style={{ background:C.cream, minHeight:"100vh", display:"grid", placeItems:"center", color:C.navy, fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif", textAlign:"center" }}>
          <div>
            <p style={{ fontSize:18, marginBottom:16 }}>That track was not found.</p>
            <Link to="/workbooks" style={{ color:C.blue, fontWeight:700 }}>Back to the library</Link>
          </div>
        </div>
      </>
    );
  }

  // Owner: the redirect effect is sending them to the library. Render nothing
  // so the buy page never flashes.
  if (owned) return null;

  // Membership is the bundle a workbook belongs to, resolved by the server.
  const trackWbs = workbooksForBundle(cfg, workbooks);

  const totalSections = trackWbs.reduce((a, w) => a + (w.sectionCount || 0), 0);
  const freeCount = trackWbs.reduce((a, w) => a + (w.freeSections || 0), 0);
  const estHours = Math.max(1, Math.round(totalSections * 0.5));

  // The workbook SKU for this bundle: what this page sells. It sells the
  // workbooks, never the video course that also carries the track, which are
  // separately priced. No product means nothing is on sale here yet.
  const product = workbookProductFor(cfg);
  const notReady = cfg.comingSoon || !product;

  // The bundle's own "what you get" list, with the live workbook count in front
  // when the bundle counts its workbooks as the headline item.
  const railIncludes = cfg.includesFoundation
    ? cfg.includes || []
    : [`${trackWbs.length} workbooks`, ...(cfg.includes || [])];

  // Enrolled count, keyed by bundle slug, from the API. The per-track default
  // that used to be invented here is gone; the baselines live with the real
  // counts on the server.
  const enrolled =
    !notReady && bundleStats && bundleStats[cfg.slug] != null
      ? bundleStats[cfg.slug].enrolled
      : undefined;

  // Weighted star rating across this bundle's workbooks (same method as catalog).
  let ratingWeighted = 0;
  let ratingTotal = 0;
  for (const w of trackWbs) {
    if (w.avgRating != null && w.ratingCount > 0) {
      ratingWeighted += w.avgRating * w.ratingCount;
      ratingTotal += w.ratingCount;
    }
  }
  const bundleRating = ratingTotal > 0 ? Math.round((ratingWeighted / ratingTotal) * 10) / 10 : null;

  // Rail meta rows. Enrolled sits right after the workbooks/sections row.
  const metaRows = [
    [I.Book, "Beginner to intermediate"],
    [I.Layers, `${trackWbs.length} workbooks, ${totalSections} sections`],
    ...(enrolled != null ? [[I.Users, `${enrolled} enrolled`]] : []),
    [I.Clock, `About ${estHours} hours`],
    [I.Refresh, "Updated regularly"],
  ];

  // The price shown is the price charged: both are this product's Price.
  const priceVal = rupees(product?.price);

  const buy = () =>
    startCheckout({
      productCode: product?.code,
      title: product?.title,
      navigate,
      // Re-read ownership in place instead of a full navigation. The redirect
      // effect above then moves the new owner to their library.
      onSettled: refreshOwnership,
    });

  // Toggle a workbook card, lazy-fetching its sections on first expand only.
  const toggleWb = (slug) => {
    const next = new Set(expandedSlugs);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    const params = new URLSearchParams(searchParams);
    if (next.size === 0) params.delete("open");
    else params.set("open", [...next].join(","));
    // replace, for the same reason as the tabs: expanding a row is not a place.
    setSearchParams(params, { replace: true });
    if (sectionsBySlug[slug] === undefined) {
      setSectionsBySlug((m) => ({ ...m, [slug]: "loading" }));
      workbooksApi
        .getOne(slug)
        .then((data) => setSectionsBySlug((m) => ({ ...m, [slug]: data?.sections || [] })))
        .catch(() => setSectionsBySlug((m) => ({ ...m, [slug]: [] })));
    }
  };

  return (
    <>
      <Navbar />
      <div style={{ background:C.cream, minHeight:"100vh", color:C.navy, fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
          .bd *{box-sizing:border-box;}
          .bd .frns{font-family:'Fraunces',serif;}
          .bd .cta:hover{background:#1d4fd0;}
          .bd .ghost:hover{color:${C.blue};}
          .bd .wcard:hover{border-color:${C.blue};}
          .bd .srow:hover{background:#faf8f3;}
          @media(max-width:900px){.bd .grid{grid-template-columns:1fr !important;}.bd .rail{position:static !important;}}
        `}</style>

        <div className="bd" style={{ maxWidth:1180, margin:"0 auto", padding:"40px 24px 80px" }}>
          <WorkbookBreadcrumb
            items={[{ label: "Workbooks", to: "/workbooks" }, { label: cfg.title }]}
            backTo="/workbooks"
            backLabel="Back to library"
          />
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, marginTop:18 }}>
            <span style={{ fontSize:12, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:cfg.accent }}>{cfg.subtitle}</span>
            <div style={{ display:"flex", gap:22, color:C.muted, fontSize:14, fontWeight:600 }}>
              <span className="ghost" style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}><I.Bookmark size={15}/> Save</span>
              <span className="ghost" style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}><I.Share size={15}/> Share</span>
            </div>
          </div>

          <h1 className="frns" style={{ fontSize:44, lineHeight:1.08, fontWeight:600, margin:"0 0 14px", maxWidth:760 }}>{cfg.title}</h1>
          <p style={{ fontSize:18, color:C.muted, maxWidth:640, margin:"0 0 36px", lineHeight:1.5 }}>{cfg.description}</p>

          <div className="grid" style={{ display:"grid", gridTemplateColumns:"1fr 360px", gap:40, alignItems:"start" }}>
            <div style={{ minWidth:0 }}>
              <div style={{ background:C.navy, borderRadius:14, padding:"40px 40px 44px", color:"#fff", marginBottom:36, minWidth:0, maxWidth:"100%" }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontStyle:"italic", fontSize:15, color:"#8ea2c4", marginBottom:18 }}>from manual click to one line of code</div>
                <div style={{ background:C.navySoft, borderLeft:`3px solid ${C.blue}`, borderRadius:8, padding:"18px 20px", fontFamily:"ui-monospace,monospace", fontSize:13.5, lineHeight:1.7, color:"#cdd6e6", overflowX:"auto", whiteSpace:"pre" }}>
                  {(cfg.heroCode || []).map((line, li) => (
                    <div key={li}>
                      {line.map((tok, ti) => {
                        const col = HCODE[tok[1]];
                        return col ? <span key={ti} style={{ color:col }}>{tok[0]}</span> : <span key={ti}>{tok[0]}</span>;
                      })}
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", gap:28, marginTop:26, flexWrap:"wrap" }}>
                  {[[trackWbs.length,"Workbooks"],[totalSections,"Sections"],[freeCount,"Free to read"],[estHours+"h","Est. time"]].map(([n,l])=>(
                    <div key={l}><div className="frns" style={{ fontSize:26, fontWeight:700 }}>{n}</div><div style={{ fontSize:12, letterSpacing:1, textTransform:"uppercase", color:"#8ea2c4" }}>{l}</div></div>
                  ))}
                </div>
              </div>

              <div style={{ display:"flex", gap:28, borderBottom:`1px solid ${C.line}`, marginBottom:28 }}>
                {[["info","Overview"],["reviews","Reviews"]].map(([k,l])=>(
                  <button key={k} onClick={()=>setTab(k)} style={{ background:"none", border:"none", cursor:"pointer", padding:"0 0 14px", fontSize:15, fontWeight:600, fontFamily:"inherit", color:tab===k?C.blue:C.muted, borderBottom:tab===k?`2px solid ${C.blue}`:"2px solid transparent", marginBottom:-1 }}>{l}</button>
                ))}
              </div>

              {tab==="info" ? (
                <>
                  {(cfg.about || []).map((p,i)=>(<p key={i} style={{ fontSize:16.5, lineHeight:1.6, margin:"0 0 14px", color:"#2c2c3a" }}>{p}</p>))}

                  <h2 className="frns" style={{ fontSize:26, fontWeight:600, margin:"34px 0 18px" }}>What you will learn</h2>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px 28px" }}>
                    {(cfg.outcomes || []).map(x=>(
                      <div key={x} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                        <span style={{ flexShrink:0, marginTop:2, width:18, height:18, borderRadius:5, background:"#e7efff", display:"grid", placeItems:"center" }}><I.Check size={12} color={C.blue} strokeWidth={3}/></span>
                        <span style={{ fontSize:14.5, lineHeight:1.45 }}>{x}</span>
                      </div>
                    ))}
                  </div>

                  <h2 className="frns" style={{ fontSize:26, fontWeight:600, margin:"40px 0 8px" }}>What is inside this track</h2>
                  <div style={{ fontSize:13, color:C.muted, marginBottom:18 }}>{trackWbs.length} workbooks, {totalSections} sections, {freeCount} free to read first</div>

                  {trackWbs.length === 0 ? (
                    <div style={{ border:`1px dashed ${C.line}`, borderRadius:12, padding:"28px", textAlign:"center", color:C.muted }}>
                      Workbooks for this track are coming soon.
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                      {trackWbs.map((w)=>{
                        const isOpen = expandedSlugs.has(w.slug);
                        const secs = sectionsBySlug[w.slug];
                        return (
                          <div key={w.slug} style={{ border:`1px solid ${C.line}`, borderRadius:12, background:C.card, overflow:"hidden" }}>
                            <button onClick={()=>toggleWb(w.slug)} style={{ width:"100%", background:"none", border:"none", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:14, padding:"16px 18px", fontFamily:"inherit", color:C.navy }}>
                              <span style={{ flexShrink:0, width:30, height:30, borderRadius:8, background:"#eef0f4", color:C.navy, display:"grid", placeItems:"center" }}><I.Book size={15} color={C.navy}/></span>
                              <div style={{ flex:1 }}>
                                <div className="frns" style={{ fontSize:17, fontWeight:600 }}>{w.title}</div>
                                <div style={{ fontSize:12.5, color:C.muted }}>{(w.sectionCount||0)} sections, {(w.freeSections||0)} free</div>
                              </div>
                              <span style={{ flexShrink:0, color:C.muted, display:"inline-flex", transform:isOpen?"rotate(180deg)":"none", transition:"transform .15s" }}><I.Down size={18}/></span>
                            </button>
                            {isOpen && (
                              <div style={{ borderTop:`1px solid ${C.line}` }}>
                                {secs === undefined || secs === "loading" ? (
                                  <div style={{ padding:"14px 18px", color:C.muted, fontSize:13.5 }}>Loading sections...</div>
                                ) : secs.length === 0 ? (
                                  <div style={{ padding:"14px 18px", color:C.muted, fontSize:13.5 }}>No sections yet.</div>
                                ) : (
                                  secs.map((s, idx)=>{
                                    const free = !!s.isFree;
                                    return (
                                      <div
                                        key={s.id ?? s.sectionSlug ?? idx}
                                        className={free ? "srow" : undefined}
                                        onClick={free ? ()=>navigate("/workbooks/"+w.slug) : undefined}
                                        style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 18px", borderTop: idx===0 ? "none" : `1px solid ${C.line}`, cursor:free?"pointer":"default" }}
                                      >
                                        <span style={{ flexShrink:0, width:24, fontSize:12, fontWeight:700, color:C.muted }}>{s.number != null ? String(s.number).padStart(2,"0") : ""}</span>
                                        <span style={{ flex:1, fontSize:14, color:free?C.navy:"#9a8f7e" }}>{s.title}</span>
                                        {free ? (
                                          <span style={{ flexShrink:0, fontSize:11, fontWeight:700, color:"#16855c", background:"#e8f5ee", padding:"2px 9px", borderRadius:20 }}>Preview</span>
                                        ) : (
                                          <span style={{ flexShrink:0, display:"inline-flex" }}><I.Lock size={14} color="#b8ad9e"/></span>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding:"30px 0", color:C.muted, fontSize:15 }}>No reviews yet. Notes from engineers who worked through this track will appear here.</div>
              )}
            </div>

            {/* rail */}
            <div className="rail" style={{ position:"sticky", top:24, display:"flex", flexDirection:"column", gap:16 }}>
              <div style={{ background:C.card, border:`1px solid ${C.line}`, borderRadius:14, padding:24 }}>
                {notReady ? (
                  <>
                    <div className="frns" style={{ fontSize:26, fontWeight:700 }}>Coming soon</div>
                    <div style={{ fontSize:13.5, color:C.muted, margin:"8px 0 4px", lineHeight:1.5 }}>This track is being built. Check back soon.</div>
                  </>
                ) : (
                  <>
                    {priceVal != null ? (
                      <div style={{ display:"flex", alignItems:"baseline", gap:10 }}>
                        <span className="frns" style={{ fontSize:34, fontWeight:700 }}>{inr(priceVal)}</span>
                      </div>
                    ) : (
                      <div className="frns" style={{ fontSize:26, fontWeight:700 }}>View details</div>
                    )}
                    <div style={{ fontSize:13, color:C.muted, margin:"6px 0 20px" }}>Inclusive of all taxes</div>
                    <button className="cta" onClick={buy} style={{ width:"100%", background:C.blue, color:"#fff", border:"none", borderRadius:10, padding:"14px", fontSize:15.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"background .15s ease" }}>
                      Get this track
                    </button>
                    {freeCount>0 && <div style={{ fontSize:12.5, color:C.muted, textAlign:"center", marginTop:12 }}>{freeCount} sections free to read first</div>}
                  </>
                )}

                <div style={{ borderTop:`1px solid ${C.line}`, marginTop:22, paddingTop:18, display:"flex", flexDirection:"column", gap:14 }}>
                  {bundleRating != null && (
                    <div style={{ display:"flex", alignItems:"center", gap:12, fontSize:14, color:"#2c2c3a" }}>
                      <span style={{ width:16, display:"inline-flex", justifyContent:"center", color:C.orange, fontSize:16 }}>★</span>
                      <span><strong style={{ fontWeight:700 }}>{bundleRating.toFixed(1)}</strong> <span style={{ color:C.muted }}>({ratingTotal})</span></span>
                    </div>
                  )}
                  {metaRows.map(([Icon,label],i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:12, fontSize:14, color:"#2c2c3a" }}><Icon size={16} color={C.muted}/> {label}</div>
                  ))}
                </div>
              </div>

              <div style={{ background:C.card, border:`1px solid ${C.line}`, borderRadius:14, padding:20 }}>
                <div style={{ fontSize:12, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:C.muted, marginBottom:14 }}>Written by</div>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ flexShrink:0, width:42, height:42, borderRadius:"50%", background:C.navy, color:"#fff", display:"grid", placeItems:"center", fontSize:14, fontWeight:700 }}>PR</div>
                  <div>
                    <div className="frns" style={{ fontSize:16, fontWeight:600, lineHeight:1.2 }}>Prasath</div>
                    <div style={{ fontSize:13, fontWeight:600, color:C.navy, marginTop:2 }}>Founder, ScriptedCAD</div>
                    <div style={{ fontSize:12, color:C.muted, lineHeight:1.4, marginTop:2 }}>CAD Automation Expert and AI in CAD enthusiast</div>
                  </div>
                </div>
              </div>

              <div style={{ background:C.card, border:`1px solid ${C.line}`, borderRadius:14, padding:20 }}>
                <div style={{ fontSize:12, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:C.muted, marginBottom:14 }}>What is inside</div>
                <div style={{ display:"flex", flexDirection:"column", gap:11 }}>
                  {railIncludes.map(x=>(<div key={x} style={{ display:"flex", gap:10, alignItems:"flex-start", fontSize:14, lineHeight:1.4 }}><I.Check size={15} color={C.blue} strokeWidth={3}/> <span>{x}</span></div>))}
                </div>
              </div>

              <div style={{ background:C.card, border:`1px solid ${C.line}`, borderRadius:14, padding:20 }}>
                <div style={{ fontSize:12, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:C.muted, marginBottom:14 }}>Who it is for</div>
                <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                  {(cfg.audience || []).map(x=>(<div key={x} style={{ fontSize:14, color:"#2c2c3a", display:"flex", gap:9 }}><span style={{ color:cfg.accent }}>&bull;</span> {x}</div>))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
